// Invites a mixed-funds Paychex 401(k) roll-in user who took the single-IRA path
// at onboarding (Individual401kPartialRollover) to roll over the fund type they
// left behind. Suppression rules: unopenedPaychexRolloverTypeSelector.
import React from "react";
import { get } from "lodash";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { FiPlusCircle } from "react-icons/fi";
import { accountConstants, userConstants } from "actions/types";
import { initializePaychexRollover } from "actions/userActions";
import { setAccountSession } from "actions/accountActions";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import {
  paychexRolloverIsCompleteSelector,
  paychexRolloverIsPendingSelector,
  unopenedPaychexRolloverTypeSelector,
} from "store/selectors/user";
import {
  accountTypesToEnglishMapping,
  ROTH_IRA,
  rolloverAccountTypesToEnglishMapping,
  TRADITIONAL_IRA,
} from "statics/accountTypes";
import type { RootState } from "types/state";

import Button from "components/Button";
import IconBanner from "components/IconBanner";

const actions = [
  userConstants.INITIALIZE_PAYCHEX_ROLLOVER,
  accountConstants.SET_ACCOUNT_SESSION,
];

const loadingSelector = createLoadingSelector(actions);
const errorSelector = createErrorSelector(actions);

const mapStateToProps = (state: RootState) => {
  return {
    unopenedAccountType: unopenedPaychexRolloverTypeSelector(state),
    firstRolloverIsPending: paychexRolloverIsPendingSelector(state),
    firstRolloverIsComplete: paychexRolloverIsCompleteSelector(state),
    isSubmitting: loadingSelector(state),
    error: errorSelector(state),
  };
};

const mapDispatchToProps = {
  initializePaychexRollover,
  setAccountSession,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<PropsFromRedux>;

class PaychexSecondRolloverCard extends React.Component<Props> {
  _openSecondAccount = () => {
    const { unopenedAccountType } = this.props;

    if (!unopenedAccountType) {
      return;
    }

    window.analytics.track("Paychex Second Rollover Started", {
      accountType: unopenedAccountType,
    });

    void this.props
      .initializePaychexRollover(
        this.props.client as ApolloClient<object>,
        unopenedAccountType
      )
      .then(async (response) => {
        // dispatchFullLifecycle resolves to the success OR failure action; only
        // success carries `data`, so a missing id means errorSelector has the
        // failure and renders the alert below.
        const accountId = get(response, "data.initializePaychexRollover.id") as
          | string
          | undefined;

        if (!accountId) {
          return;
        }

        await this.props.setAccountSession(
          this.props.client as ApolloClient<object>,
          accountId
        );

        window.analytics.track(
          "Submitted Paychex Second Rollover Account Type"
        );
        // PrivateRoute onboards off the active account session, which is only
        // re-read on a fresh boot
        window.location.reload();
      })
      .catch((error: unknown) => {
        console.error("An error occurred:", error);
      });
  };

  render() {
    const { unopenedAccountType } = this.props;

    if (!unopenedAccountType) {
      return null;
    }

    const openedAccountType =
      unopenedAccountType === TRADITIONAL_IRA ? ROTH_IRA : TRADITIONAL_IRA;

    const unopenedFunds =
      rolloverAccountTypesToEnglishMapping[unopenedAccountType];
    const unopenedAccount = accountTypesToEnglishMapping[unopenedAccountType];
    const openedAccount = accountTypesToEnglishMapping[openedAccountType];

    const title = `Your Paychex 401(k) still has ${unopenedFunds.toLowerCase()} savings to roll over.`;

    const needsAccount = `Your ${unopenedFunds.toLowerCase()} savings need a ${unopenedAccount} to keep the same tax benefits`;

    // A roll-in member can reach this invite with no rollover record at all — the
    // records lag the account, and for a partial rollover never land — hence the
    // third variant that claims no first rollover.
    let subheader;
    if (this.props.firstRolloverIsPending) {
      subheader = `You're rolling over your ${openedAccount} savings now. ${needsAccount} — you can open one and roll them over too.`;
    } else if (this.props.firstRolloverIsComplete) {
      subheader = `You rolled over your ${openedAccount} savings. ${needsAccount} — open one to roll them over too.`;
    } else {
      subheader = `${needsAccount} — open one to roll them over.`;
    }

    return (
      <IconBanner
        icon={<FiPlusCircle color="white" stroke="#60A4BF" size={16} />}
        color="primary"
        title={title}
        subheader={subheader}
        error={!!this.props.error}
        errorMsg={this.props.error}
        actionComponent={
          <Button
            size="sm"
            color="action"
            name="action"
            btnLabel={`Open ${unopenedAccount}`}
            loading={this.props.isSubmitting}
            onClick={this._openSecondAccount}
          />
        }
      />
    );
  }
}

export default connector(withApollo<Props>(PaychexSecondRolloverCard));
